import { useBadInventoryFilterAPI } from "@/app/hooks/api_hooks/Inventory/BadInventory/useBadInventoryFilterAPI";
import BadInventoryRequestsFilter from "./BadInventoryRequestsFilter";
import { useEffect, useRef, useState } from "react";
import { IBadInventoryProductFilterBody } from "@/api_framework/api_modals/Inventory";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useLoader } from "@/app/hooks/useLoader";
import { IoEllipsisHorizontalCircleOutline } from "react-icons/io5";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useBadInventoryActionsAPI } from "@/app/hooks/api_hooks/Inventory/BadInventory/useBadInventoryActionsAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import PaginationComponent from "@/app/components/ui/Pagination";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { handleActionByKey, useUtils } from "@/app/hooks/useUtils";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
const PageSize = 10;

const BadInventoryRequests = () => {
  const [_loading, setLoading] = useState(true);
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [badProductFilters, setBadProductFilters] = useState<IBadInventoryProductFilterBody>();
  const { execute: badInventoryProductFilterApi, data } = useBadInventoryFilterAPI();
  const configuration = useConfiguration();
  const isProductMoveGoodToBadInProgess = useLoader("isInventoryProductBadInventoryRequest");
  const { setVisible: imageView, setModalData } = useModalActions("BAD_INVENTORY_VIEW_IMAGE");
  const { setVisible: goodToBad, setModalData: goodToBadData } = useModalActions("GOOD_TO_BAD_MODAL");
  const dropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [_shouldCallAPI, setShouldCallAPI] = useState(true);
  const { setVisible: rejectRequest, setModalData: setRejectRequestModalData } =
    useModalActions("REJECT_REQUEST_MODAL");
  const { setVisible: shrinkImage, setModalData: setShrinkImageModalData } = useModalActions("SHRINK_IMAGE_MODAL");
  const { setVisible: invalidImage, setModalData: setInvalidImageModalData } = useModalActions("INVALID_IMAGE");
  const [expandedItemIds, setExpandedItemIds] = useState<{ [key: string]: boolean }>({});
  const [overflowMap, setOverflowMap] = useState<{ [key: string]: boolean }>({});
  const reasonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat } = useUtils();

  const toggleExpand = (id: string) => {
    setExpandedItemIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const newOverflowMap: { [key: string]: boolean } = {};
    data?.data?.forEach((item) => {
      const ref = reasonRefs.current[item.bad_inventory_id];
      if (ref) {
        const lineHeight = parseInt(getComputedStyle(ref).lineHeight || "16", 10);
        const maxLines = 3;
        const maxHeight = lineHeight * maxLines;
        newOverflowMap[item.bad_inventory_id] = ref.scrollHeight > maxHeight;
      }
    });
    setOverflowMap(newOverflowMap);
  }, [data]);

  const refreshData = () => {
    const clinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
    const clinicIds =
      Array.isArray(badProductFilters?.clinic_ids) && badProductFilters?.clinic_ids.length > 0
        ? badProductFilters.clinic_ids
        : [clinicId];
    const filterBody = {
      ...badProductFilters,
      clinic_ids: clinicIds,
    };
    setLoading(true);
    badInventoryProductFilterApi(filterBody, currentPage, PageSize).finally(() => setLoading(false));
  };

  const { execute: actionsOnBad } = useBadInventoryActionsAPI(refreshData);

  const handleRejectRequestClick = (item) => {
    setShouldCallAPI(false);
    setRejectRequestModalData({
      bad_inventory_id: item.bad_inventory_id,
      badProductFilters: badProductFilters,
      refreshData,
    });
    rejectRequest();
    setShouldCallAPI(true);
  };

  const handleShrinkImageClick = (item) => {
    setShouldCallAPI(false);
    setShrinkImageModalData({
      bad_inventory_id: item.bad_inventory_id,
      badProductFilters: badProductFilters,
      refreshData,
    });
    shrinkImage();
    setShouldCallAPI(true);
  };

  const handleInvalidImageClick = (item) => {
    setShouldCallAPI(false);
    setInvalidImageModalData({
      bad_inventory_id: item.bad_inventory_id,
      badProductFilters: badProductFilters,
      refreshData,
    });
    invalidImage();
    setShouldCallAPI(true);
  };

  const handleActionsClick = (item, key) => {
    setShouldCallAPI(false);
    if (key && item && key !== "re_upload_image") {
      actionsOnBad({ bad_inventory_id: item.bad_inventory_id, action: key });
    }
    if (key == "re_upload_image") {
      goodToBadData({
        key: "re_upload_image",
        vetic_variant_id: item.vetic_variant_id,
        grn_id: item.grn_id,
        sub_grn_id: item.sub_grn_id,
        category_id: item.category,
        bad_qty: item.bad_inventory_quantity,
        bad_reason: item.reason_of_inventory_movement,
        re_upload_image: item.re_upload_image,
        sub_category_id: item.sub_category_id,
        unique_row_id: item.unique_row_id,
        bad_inventory_id: item.bad_inventory_id,
        product_name: item.product_name,
        lot_number: item.lot_number,
        manufacturer_name: item.manufacturer_name,
        expiry_date: item.expiry_date,
        mrp: item.mrp,
      });
      goodToBad();
      setShouldCallAPI(true);
    }
    setActiveRow(null);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveRow(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (badProductFilters?.clinic_ids) {
      const clinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
      const filterBody = {
        ...badProductFilters,
        clinic_ids:
          Array.isArray(badProductFilters?.clinic_ids) && badProductFilters?.clinic_ids?.length > 0
            ? badProductFilters.clinic_ids
            : [clinicId],
      } as IBadInventoryProductFilterBody;
      setLoading(true);
      badInventoryProductFilterApi(filterBody, currentPage, PageSize).finally(() => setLoading(false));
    }
  }, [badProductFilters, localStorage.getItem(configuration.localStorage.CLINIC.key), currentPage, PageSize]);

  const handleImageClick = (e, images) => {
    e.preventDefault();
    e.stopPropagation();
    setModalData({ images });
    imageView();
  };

  // RBAC seeding for bad-inventory actions — legacy roles per action:
  //   back_to_good       → clinic_ops, clinic_ops_doggyworld
  //   disposable         → central_ops
  //   invalid_image      → central_ops
  //   non_disposable     → central_ops
  //   reject_request     → central_ops
  //   shrink             → clinic_ops, clinic_ops_doggyworld
  //   re_upload_image    → clinic_ops, clinic_ops_doggyworld
  const badInventoryActionAccess: Record<string, boolean> = {
    back_to_good: useFeatureAccess("BAD_INVENTORY_BACK_TO_GOOD_ACTION"),
    disposable: useFeatureAccess("BAD_INVENTORY_DISPOSABLE_ACTION"),
    invalid_image: useFeatureAccess("BAD_INVENTORY_INVALID_IMAGE_ACTION"),
    non_disposable: useFeatureAccess("BAD_INVENTORY_NON_DISPOSABLE_ACTION"),
    reject_request: useFeatureAccess("BAD_INVENTORY_REJECT_REQUEST_ACTION"),
    shrink: useFeatureAccess("BAD_INVENTORY_SHRINK_ACTION"),
    re_upload_image: useFeatureAccess("BAD_INVENTORY_REUPLOAD_IMAGE_ACTION"),
  };

  const shouldRenderActionMenu = (actions) => {
    return (
      Object.entries(actions)?.filter(([key, _action]) => {
        return badInventoryActionAccess[key];
      })?.length > 0
    );
  };

  const handleIconClick = (rowIndex: number) => {
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending Action":
        return { color: "#A47A0D" };
      case "Disposable":
        return { color: "#1D63B6" };
      case "Non Disposable":
        return { color: "#DB1E4C" };
      case "Rejected Request":
        return { color: "#8F00FF" };
      case "Invalid Image":
        return { color: "#00A67E" };
      default:
        return {};
    }
  };

  const hasBadInventoryData = Array.isArray(data?.data) ? data?.data?.length > 0 : data?.data;

  return (
    <>
      <div className="rounded-md bg-white px-2 py-3">
        <BadInventoryRequestsFilter setBadProductFilters={setBadProductFilters} />
      </div>
      <div className="rounded-md bg-white px-4">
        {isProductMoveGoodToBadInProgess ? (
          <TableSkeleton />
        ) : (
          <div>
            {hasBadInventoryData ? (
              <div className="w-full mt-3">
                <div className="w-full rounded-xl bg-[#F5F5F5] px-[3px] pb-[1px]">
                  <div className="w-full overflow-x-auto rounded-xl">
                    <div className="inline-block min-w-full py-1 align-middle">
                      <table className="min-w-max w-full border-separate border-spacing-x-0 border-spacing-y-0.5">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr className="py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize px-3">
                            <th className="px-3 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Product Name
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Clinic
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Category
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Lot #
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Manufacturer
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Exp Date
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              MRP (Tax Inclusive)
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Qty
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Image Proof
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Reason for Bad
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs text-black font-medium">
                              <div className="w-24 whitespace-nowrap">Creation date & time</div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs whitespace-nowrap text-black font-medium">
                              Created By
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Action Menu
                            </th>
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {Array.isArray(data?.data) && data?.data?.length > 0 ? (
                            data?.data?.map((item, index) => (
                              <tr key={index} className="group bg-white transition">
                                <td className="px-3 py-4 text-xs font-normal text-left">{item.product_name}</td>
                                <td className="px-6 py-4 text-xs font-normal text-left">{item.clinic_name}</td>
                                <td className="px-6 py-4 text-xs font-normal text-left">{item.category}</td>
                                <td className="px-6 py-4 text-xs font-normal text-left">{item.lot_number}</td>
                                <td className="px-6 py-4 text-xs font-normal text-left">{item.manufacturer_name}</td>
                                <td className="px-6 whitespace-nowrap py-4 text-xs font-normal text-left">
                                  {veticDateFormat(item.expiry_date)}
                                </td>
                                <td className="px-6 py-4 text-xs font-normal text-left">{item.mrp}</td>
                                <td className="px-6 py-4 text-xs font-normal text-left">
                                  {item.bad_inventory_quantity}
                                </td>
                                <td className="px-6 py-4 text-xs font-normal text-left">
                                  {item.inventory_movement_images.length > 0 && (
                                    <div className="flex items-center">
                                      <button
                                        onClick={(e) => handleImageClick(e, item.inventory_movement_images)}
                                        className="text-blue-500 hover:underline font-medium"
                                      >
                                        View
                                      </button>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-xs font-normal text-left">
                                  {item.reason_of_inventory_movement}
                                </td>
                                <td
                                  className="px-6 py-4 text-xs font-medium text-left"
                                  style={getStatusColor(item.inventory_movement_status)}
                                >
                                  <div>{item.inventory_movement_status}</div>
                                  {item.inventory_movement_status === "Rejected Request" &&
                                    item.reason_of_rejection && (
                                      <div className="mt-1 text-[10px] text-black">
                                        <div
                                          ref={(el) => (reasonRefs.current[item.bad_inventory_id] = el)}
                                          className={
                                            expandedItemIds[item.bad_inventory_id] ? "" : "line-clamp-3 overflow-hidden"
                                          }
                                        >
                                          {item.reason_of_rejection}
                                        </div>
                                        {overflowMap[item.bad_inventory_id] && (
                                          <button
                                            type="button"
                                            className="mt-1 text-blue-500 underline text-[10px]"
                                            onClick={() => toggleExpand(item.bad_inventory_id)}
                                          >
                                            {expandedItemIds[item.bad_inventory_id] ? "View Less" : "View More"}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  {item.inventory_movement_status === "Invalid Image" &&
                                    item.reason_of_invalid_image && (
                                      <div className="mt-1 text-[10px] text-black">
                                        <div
                                          ref={(el) => (reasonRefs.current[item.bad_inventory_id] = el)}
                                          className={
                                            expandedItemIds[item.bad_inventory_id] ? "" : "line-clamp-3 overflow-hidden"
                                          }
                                        >
                                          {item.reason_of_invalid_image}
                                        </div>
                                        {overflowMap[item.bad_inventory_id] && (
                                          <button
                                            type="button"
                                            className="mt-1 text-blue-500 underline text-[10px]"
                                            onClick={() => toggleExpand(item.bad_inventory_id)}
                                          >
                                            {expandedItemIds[item.bad_inventory_id] ? "View Less" : "View More"}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-xs font-normal text-left">
                                  {item.inventory_movement_at}
                                </td>
                                <td className="px-6 py-4 text-xs font-normal text-left">
                                  {item.inventory_moved_to_bad_by}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-xs font-normal text-center relative">
                                  {shouldRenderActionMenu(item.dropdown_actions) && (
                                    <div>
                                      <IoEllipsisHorizontalCircleOutline
                                        fontSize={24}
                                        color="rgb(128,129,131)"
                                        className="cursor-pointer"
                                        onClick={() => handleIconClick(index)}
                                      />
                                      {activeRow === index && (
                                        <div
                                          ref={dropdownRef}
                                          className="bg-white shadow-md p-3 z-10 w-48 rounded-sm absolute right-0 text-left"
                                        >
                                          {Object.entries(item.dropdown_actions).map(
                                            ([key, action], index) =>
                                              badInventoryActionAccess[key] && (
                                                <p
                                                  key={index}
                                                  data-action-key={key}
                                                  onClick={() =>
                                                    handleActionByKey(key, item, {
                                                      handleRejectRequestClick,
                                                      handleShrinkImageClick,
                                                      handleInvalidImageClick,
                                                      handleActionsClick,
                                                    })
                                                  }
                                                  className="pt-3 cursor-pointer"
                                                >
                                                  {String(action)}
                                                </p>
                                              ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td></td>
                              <td></td>
                              <td colSpan={8} className=" py-4 text-xs text-center text-gray-500">
                                No results found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NoRecordFoundComponent
                imageURL={NO_PWL_RESULTS}
                message="No Bad Inventory Data Found"
                description="No bad inventory data found for the given filters, try something else"
              />
            )}
          </div>
        )}
        {badProductFilters?.clinic_ids && (
          <div className="flex justify-center items-center w-auto py-5">
            <PaginationComponent
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={data?.item_counts | 0}
              pageSize={PageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default BadInventoryRequests;
